import { computedFrom, observable } from "aurelia-binding";
import { autoinject, transient } from "aurelia-dependency-injection";
import * as Enumerable from "linq";
import * as moment from "moment";
import { VetRuleOutcomeStepEnum } from "../../../../../../models/vetting/vetting.models";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { SignalFunctions } from "../../../../../../services/basics/signal-functions";
import { VettingService } from "../../../../../../services/vetting/vetting.service";
import { DailyRiskHistory, DailyRiskHistoryItemWithRiskLevelModel, VesselPreArrivalDetailsModel, VetRunRiskLevelsEnum } from "../../models/ports-and-terminals.models";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { PortsAndTerminalsService } from "../../services/ports-and-terminals.service";
import { useView } from "aurelia-framework";

@autoinject()
@transient()
@useView('views/pages/modules/ports-and-terminals/vessel-pre-arrival/steps/vessel-pre-arrival-risk-history.html')
export class VesselPreArrivalRiskHistory {

    //Current vet rules runs
    runs: DailyRiskHistory[] = [];

    filter: string;

    //Runs data is being loaded
    runsLoading: boolean = true;

    high: boolean = true;
    medium: boolean = true;
    low: boolean = true;
    showRiskCategoryFilter: boolean = false;
    isMobileFilterOpen: boolean;
    selectedRiskCategories: string[] = [];
    changed: boolean = true;
    vetId: number;
    estimatedArrivalTime: string;

    lastSevenDays: { run: DailyRiskHistory; date: string, previousDate: string }[] = [];

    @observable
    selectedDay: { run: DailyRiskHistory; date: string, previousDate: string };

    /**
    * Update current tab's vet history
    * @param params
    */
    vetTasksUpdated: (params: { vetId: number }) => void = (params: { vetId: number }) => {
        if (this.vetId === params.vetId) this.reload();
    };

    //Filter toggle functions
    toggleHigh() {
        this.high = !this.high;
    }
    toggleMedium() { this.medium = !this.medium; }
    toggleLow() { this.low = !this.low; }
    toggleChanged() { this.changed = !this.changed; }
    toggleRiskCategoryFilters() { this.showRiskCategoryFilter = !this.showRiskCategoryFilter; }

    /**
    * Hide/show mobile filter dialog & inner sections
    */
    toggleMobileFilterDialog() {
        if (!this.isMobileSubFilterOpen) {
            this.isMobileFilterOpen = !this.isMobileFilterOpen;
        }

        this.showRiskCategoryFilter = false;
    }

    /**
    * Clear risk category filter
    */
    clearRiskCategoryFilter() {
        this.selectedRiskCategories = [];
        this.showRiskCategoryFilter = false;
    }

    /**
    * Apply risk category filter
    * @param items Selected risk categories
    */
    public applyRiskCategoryFilter(items: any[]) {
        this.selectedRiskCategories = items.map(x => x.name);
        this.showRiskCategoryFilter = false;
    }

    /**
    * Clear all filters
    */
    clearAllFilters() {
        this.high = false;
        this.medium = false;
        this.low = false;
        this.changed = false;
        this.clearRiskCategoryFilter();
    }

    constructor(private signalClientService: SignalClientService,
        private portsAndTerminalsService: PortsAndTerminalsService,
        private vettingService: VettingService,
        private dialogClientService: DialogClientService) {

    }

    async activate(item: { vesselPreArrivalDetails: VesselPreArrivalDetailsModel }) {
        this.vetId = item.vesselPreArrivalDetails.vetId;
        this.estimatedArrivalTime = item.vesselPreArrivalDetails.estimatedArrivalTime;
        this.signalClientService.subscribe(SignalFunctions.vetTaskUpdateChanged, this.vetTasksUpdated);
    }

    detached() {
        this.signalClientService.unsubscribe(SignalFunctions.vetTaskUpdateChanged, this.vetTasksUpdated);
    }

    /**
    * Determine whether mobile sub-filter is open
    */
    public get isMobileSubFilterOpen(): boolean {
        return this.showRiskCategoryFilter;
    }

    /**
    * Reload current tab's data
    */
    async reload() {
        const arrivalDate = moment(moment(this.estimatedArrivalTime).utc().format("DD MMM YYYY HH:mm")).toDate();
        const lastDate = moment(arrivalDate) < moment() ? moment(arrivalDate) : moment();
        this.runsLoading = true;
        this.runs = (await this.portsAndTerminalsService.getRiskHistory({
            toDate: lastDate.toJSON(),
            vetId: this.vetId
        })).dailyItems;
        this.runsLoading = false;
        return this.runs;
    }

    async attached() {
        const runs = await this.reload();
        const days: { run: DailyRiskHistory; date: string, previousDate: string }[] = [];

        const arrivalDate = moment(moment(this.estimatedArrivalTime).utc().format("DD MMM YYYY HH:mm")).toDate();
        const lastDate = moment(arrivalDate) < moment() ? moment(arrivalDate) : moment();
        //Compute Vet rule runs for past 7 days
        for (let i = 6; i >= 0; i--) {
            const date = lastDate.clone().add(i * -1, "d");
            const previousDate = lastDate.clone().add((i + 1) * -1, "d");
            const run = runs.filter(a => moment(a.date).format("DD MMM YYYY") == date.format("DD MMM YYYY"))[0];
            const prevRun = runs.filter(a => moment(a.date).format("DD MMM YYYY") == previousDate.format("DD MMM YYYY"))[0];

            if (run) {
                run.groupedItems = Enumerable.from(run.items)
                    .selectMany(x => {
                        const prevItem = prevRun ? prevRun.items.filter(y => y.vetTaskId == x.vetTaskId)[0] : null;
                        if (x.scores && x.scores.length) {
                            return x.scores.map(a => {
                                const prevScore = (prevItem ? prevItem.scores : []).filter(y => y.riskCategory == a.riskCategory)[0];
                                return {
                                    riskCategory: a.riskCategory,
                                    riskLevel: a.riskLevel,
                                    riskLevelId: a.riskLevelId,
                                    previousRiskLevel: prevScore?.riskLevel,
                                    previousRiskLevelId: prevScore?.riskLevelId,
                                    previousVetTaskOutcome: prevItem ? prevItem.vetTaskOutcome : null,
                                    hasPreviousSystemError: prevItem ? prevItem.hasSystemError : false,
                                    rule: x
                                }
                            });
                        }

                        return [{
                            riskCategory: "Uncategorized",
                            riskLevel: null,
                            riskLevelId: null,
                            previousRiskLevel: null,
                            previousRiskLevelId: null,
                            previousVetTaskOutcome: null,
                            hasPreviousSystemError: null,
                            rule: x
                        }];
                    }).groupBy(x => x.riskCategory).select(x => {
                        return {
                            key: x.key(),
                            items: x.where(a => a.rule.isActive).toArray()
                        }
                    }).where(x => ["All", "Critical", "Soft"].indexOf(x.key) == -1).toArray();

                const allItems = Enumerable.from(run.groupedItems).selectMany(x => x.items).toArray();
                run.countOraFail = allItems.filter(x => this.isFailedRule(x) && x.riskLevelId == VetRunRiskLevelsEnum.Ora).length;
                run.countHighFail = allItems.filter(x => this.isFailedRule(x) && x.riskLevelId == VetRunRiskLevelsEnum.High).length;
                run.countMediumFail = allItems.filter(x => this.isFailedRule(x) && x.riskLevelId == VetRunRiskLevelsEnum.Medium).length;
                run.countLowFail = allItems.filter(x => this.isFailedRule(x) && x.riskLevelId == VetRunRiskLevelsEnum.Low).length;
                run.riskLevel = run.countOraFail > 0 ? 'ora' : run.countHighFail > 0 ? 'high' : run.countMediumFail > 0 ? 'medium' : 'low';
                run.riskLevelCount = run.riskLevel == 'ora' || run.riskLevel == 'high' ? run.countOraFail + run.countHighFail : run.riskLevel == 'medium'
                            ? run.countMediumFail: run.riskLevel == 'low'? run.countLowFail: 0;
            }

            days.push({
                date: date.format("DD MMM"),
                previousDate: previousDate.format("DD MMM"),
                run: run
            });
        }
        this.lastSevenDays = days;

        this.selectDay(this.lastSevenDays[this.lastSevenDays.length - 1]);
    }

    /**
    * Set current selectedDay
    * @param selectedDay Current selected day
    */
    async selectDay(selectedDay: { run: DailyRiskHistory; date: string, previousDate: string }) {
        if (selectedDay.run) {
            this.selectedDay = selectedDay;
        }
    }

    /**
    * Checks whether Vet rule run has pending data to be prefetched
    * @param task Vet rule run
    */
    isPendingPrefetch(task: DailyRiskHistoryItemWithRiskLevelModel) {
        return task.rule.hasPendingPrefetchData && task.rule.prefetchRuleActionId && !task.rule.hasSystemError;
    }

    @computedFrom("selectedDay.run.items")
    get vetRiskScoresEnumerable() { return Enumerable.from(this.selectedDay && this.selectedDay.run ? this.selectedDay.run.groupedItems : []).selectMany(x => x.items).where(x => x.rule.isActive); }

    @computedFrom("selectedDay.run.items")
    get countAllLow() { return this.vetRiskScoresEnumerable.count(x => !this.isFailedRule(x) || (this.isFailedRule(x) && x.riskLevelId == VetRunRiskLevelsEnum.Low)); }

    @computedFrom("selectedDay.run.items")
    get countAllMedium() { return this.vetRiskScoresEnumerable.count(x => this.isFailedRule(x) && x.riskLevelId == VetRunRiskLevelsEnum.Medium); }

    @computedFrom("selectedDay.run.items")
    get countAllHigh() { return this.vetRiskScoresEnumerable.count(x => this.isFailedRule(x) && x.riskLevelId == VetRunRiskLevelsEnum.High); }

    @computedFrom("selectedDay.run.items")
    get countAllOra() { return this.vetRiskScoresEnumerable.count(x => this.isFailedRule(x) && x.riskLevelId == VetRunRiskLevelsEnum.Ora); }

    @computedFrom("selectedDay.run.items")
    get countAllChanged() {
        return this.selectedDay && this.selectedDay.run ? this.vetRiskScoresEnumerable
            .count(x => this.isRuleChanged(x)) : 0;
    }

    /**
    * Clear filter
    */
    clearFilter() {
        this.filter = "";
    }

    /**
    * Open the additional details dialog
    * @param task Vet rule run
    */
    openAdditionalDetailsDialog(task: DailyRiskHistoryItemWithRiskLevelModel) {

        if (task.rule.hasListItems && !task.rule.hasRsListItems) {
            this.vettingService.getListItems(task.rule.vetTaskId).then(listItems => {
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AdditionalInformationDialog, {
                    listItems: listItems,
                    task: task.rule
                }, true, false, false, "right");
            });
        }
        else if (task.rule.hasCap) {

            this.vettingService.getCapResponse(this.vetId, task.rule.vetTaskId).then(cap => {
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AdditionalInformationDialog, {
                    cap: cap,
                    task: task.rule
                }, true, false, false, "right");
            });
        }
        else if (task.rule.canViewDisplayResultsInOverview) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AdditionalInformationDialog, {
                task: task.rule
            }, true, false, false, "right");
        }
    }

    hasAnyWithFilter(category: string, items: DailyRiskHistoryItemWithRiskLevelModel[]) {
        return items.filter(x => this.hasFilter(category, x)).length > 0;
    }

    hasFilter(category: string, item: DailyRiskHistoryItemWithRiskLevelModel) {
        let isVisible = !this.filter || item.rule.title.toLowerCase().indexOf(this.filter.toLowerCase()) > -1;

        if (isVisible) {
            isVisible = this.selectedRiskCategories.length == 0 || this.selectedRiskCategories.indexOf(category) > -1;

            if (this.high || this.low || this.medium || this.changed) {
                isVisible = isVisible &&
                    (this.high && (item.riskLevelId == VetRunRiskLevelsEnum.High || item.riskLevelId == VetRunRiskLevelsEnum.Ora) && this.isFailedRule(item)) ||
                    (this.low && (item.riskLevelId == VetRunRiskLevelsEnum.Low && this.isFailedRule(item) || !this.isFailedRule(item))) ||
                    (this.medium && item.riskLevelId == VetRunRiskLevelsEnum.Medium && this.isFailedRule(item)) ||
                    (this.changed && this.isRuleChanged(item));
            }
        }

        return isVisible;
    }

    isRuleChanged(item: DailyRiskHistoryItemWithRiskLevelModel) {
        const isFailedRule = this.isFailedRule(item);
        const isPreviousFailedRule = this.isPreviousFailedRule(item);
        return item.previousRiskLevelId && (
            (item.riskLevelId != item.previousRiskLevelId && isFailedRule && isPreviousFailedRule) ||
            (isFailedRule != isPreviousFailedRule)
        );
    }

    isFailedRule(item: DailyRiskHistoryItemWithRiskLevelModel) {
        return item.rule.vetTaskOutcome == VetRuleOutcomeStepEnum.Fail || item.rule.hasSystemError;
    }

    isPreviousFailedRule(item: DailyRiskHistoryItemWithRiskLevelModel) {
        return item.previousVetTaskOutcome == VetRuleOutcomeStepEnum.Fail || item.hasPreviousSystemError;
    }
}
